import { createRouter, createWebHistory, type RouteLocationGeneric } from 'vue-router'

import AboutView from '../views/AboutView.vue'
import AccountInvitationsView from '../views/AccountInvitationsView.vue'
import OfflineDataView from '../views/OfflineDataView.vue'
import StaticPageView from '../views/StaticPageView.vue'
import WorkspacesView from '../views/WorkspacesView.vue'

function redirectToRoute(name: string) {
  return (to: RouteLocationGeneric) => ({
    name,
    params: to.params,
    query: to.query,
    hash: to.hash,
  })
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      redirect: (to) => ({ path: '/pinned', query: to.query, hash: to.hash }),
    },
    {
      path: '/home',
      name: 'landing-page',
      component: WorkspacesView,
    },
    {
      path: '/library',
      name: 'library',
      component: WorkspacesView,
    },
    {
      path: '/pinned',
      name: 'pinned',
      component: WorkspacesView,
    },
    {
      path: '/account/invitations/',
      name: 'account-invitations',
      components: {
        default: WorkspacesView,
        overlay: AccountInvitationsView,
      },
      meta: { mapOverlay: true },
    },
    {
      path: '/join/:inviteCode',
      name: 'account-invite-signup',
      component: WorkspacesView,
    },
    {
      path: '/workspaces/',
      redirect: '/collections',
    },
    {
      path: '/workspaces/invitations/:token/accept',
      redirect: redirectToRoute('accept-invitation'),
    },
    {
      path: '/workspaces/:workspaceId/tour/:tourId',
      redirect: redirectToRoute('tour-detail'),
    },
    {
      path: '/workspaces/:workspaceId/photo/:photoId',
      redirect: redirectToRoute('photo-detail'),
    },
    {
      path: '/workspaces/:workspaceId',
      redirect: redirectToRoute('workspace-detail'),
    },
    {
      path: '/collections',
      name: 'workspaces',
      component: WorkspacesView,
    },
    {
      path: '/collections/invitations/:token/accept',
      name: 'accept-invitation',
      component: WorkspacesView,
      props: (route) => ({
        invitationToken: route.params.token,
      }),
    },
    {
      path: '/collections/:workspaceId',
      name: 'workspace-detail',
      component: WorkspacesView,
      props: true,
    },
    {
      path: '/collections/:workspaceId/tour/:tourId',
      name: 'tour-detail',
      component: WorkspacesView,
      props: true,
    },
    {
      path: '/collections/:workspaceId/photo/:photoId',
      name: 'photo-detail',
      component: WorkspacesView,
      props: true,
    },
    {
      path: '/accounts/reset-password/:token',
      name: 'accounts-password-reset-confirm',
      redirect: (to) => {
        return {
          path: '/',
          query: { auth: 'reset-confirm', token: to.params.token },
        }
      },
    },
    {
      path: '/about',
      name: 'about-page',
      components: {
        default: WorkspacesView,
        overlay: AboutView,
      },
      meta: { mapOverlay: true },
    },
    {
      path: '/imprint',
      name: 'imprint-page',
      components: {
        default: WorkspacesView,
        overlay: StaticPageView,
      },
      meta: { mapOverlay: true },
      props: {
        overlay: { pageId: 'imprint' },
      },
    },
    {
      path: '/privacy-policy',
      name: 'privacy-policy-page',
      components: {
        default: WorkspacesView,
        overlay: StaticPageView,
      },
      meta: { mapOverlay: true },
      props: {
        overlay: { pageId: 'privacy-policy' },
      },
    },
    {
      path: '/data-protection',
      name: 'data-protection-page',
      components: {
        default: WorkspacesView,
        overlay: StaticPageView,
      },
      meta: { mapOverlay: true },
      props: {
        overlay: { pageId: 'eu-data-protection' },
      },
    },
    {
      path: '/terms-of-use',
      name: 'terms-of-use-page',
      components: {
        default: WorkspacesView,
        overlay: StaticPageView,
      },
      meta: { mapOverlay: true },
      props: {
        overlay: { pageId: 'terms-of-use' },
      },
    },
    {
      path: '/keyboard-shortcuts',
      name: 'keyboard-shortcuts-page',
      components: {
        default: WorkspacesView,
        overlay: StaticPageView,
      },
      meta: { mapOverlay: true },
      props: {
        overlay: { pageId: 'keyboard-shortcuts' },
      },
    },
    {
      path: '/offline',
      name: 'offline-data',
      components: {
        default: WorkspacesView,
        overlay: OfflineDataView,
      },
      meta: { mapOverlay: true },
    },
    {
      path: '/offline/map-areas',
      name: 'offline-map-areas',
      component: WorkspacesView,
      meta: { mapOverlay: true, mapInteractiveOverlay: true },
    },
    {
      // Internal design-system reference; lazy-loaded so its full icon set stays out of the main bundle.
      path: '/styleguide',
      name: 'styleguide',
      component: () => import('../views/StyleguideView.vue'),
    },
  ],
})

router.beforeEach((to) => {
  if (to.path === '/library/') {
    return { path: '/library', query: to.query, hash: to.hash }
  }
  if (to.path === '/collections/') {
    return { path: '/collections', query: to.query, hash: to.hash }
  }
  if (to.path === '/pinned/') {
    return { path: '/pinned', query: to.query, hash: to.hash }
  }
})

export default router
